<template>
  <div ref="widgetRef" class="tw:!w-full tw:!flex tw:!justify-center tw:!items-center">
    <SearchWidgetInner
      v-bind="sharedState"
      v-on="sharedHandlers"
    />
  </div>

  <Teleport to="body">
    <Transition name="sticky-slide">
      <div v-if="isSticky" class="esim-sticky-navbar">
        <div class="esim-sticky-inner">
          <SearchWidgetInner
            v-bind="sharedState"
            v-on="sharedHandlers"
            compact
          />
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import CountriesData from '~/assets/countriesData.json'
import { getFlag } from '~/utility/helper'
import { useAutocompletionHelper } from '~/composables/useAutocompletionHelper'
import { useScrollSticky } from '~/composables/useScrollSticky'
import SearchWidgetInner from './search-widget-options/SearchWidgetInner.vue'
import { sleep } from '~/composables/general'

type SearchItem = { 
  name: string
  slug: string
  type: number
  flag: string
}

const emit = defineEmits<{
  'select-country': [slug: string, name: string]
}>()

const widgetRef = ref<HTMLElement | null>(null)
const query = ref('')
const isOpen = ref(false)
const inputRef = ref<HTMLInputElement | null>(null)
const selectedItem = ref<SearchItem | null>(null)
const hasError = ref(false)

const { isSticky } = useScrollSticky(widgetRef)

const PRIORITY_CODES = ['MC', 'IN', 'JP', 'ID', 'BD', 'PK', 'NG', 'GH', 'US', 'CN']

const allItems = computed(() =>
  CountriesData.map(item => ({
    name: item.name,
    slug: item.code,
    type: item.type,
    flag: getFlag(item.code),
  })).sort((a, b) =>
    +PRIORITY_CODES.includes(b.slug) - +PRIORITY_CODES.includes(a.slug)
  )
)

const results = computed(() => {
  const q = query.value.trim().toLowerCase()
  if (!q) return allItems.value.slice(0, 5)
  return allItems.value.filter(item => item.name.toLowerCase().includes(q)).slice(0, 5)
})

const autocomplete = useAutocompletionHelper(results, query, selectItem)

function selectItem(item: SearchItem) {
  query.value = item.name
  selectedItem.value = item
  hasError.value = false
  isOpen.value = false
  inputRef.value?.blur()
}

function handleFindPlan() {
  if (!selectedItem.value) {
    hasError.value = true
    return
  }
  hasError.value = false
  emit('select-country', selectedItem.value.slug, selectedItem.value.name)
}

function handleKeydown(e: KeyboardEvent) {
  switch (e.key) {
    case 'ArrowDown':
      e.preventDefault()
      autocomplete.down()
      break
    case 'ArrowUp':
      e.preventDefault()
      autocomplete.up()
      break
    case 'Enter':
      e.preventDefault()
      autocomplete.enter()
      break
    case 'Escape':
      isOpen.value = false
      break
  }
}

function clearQuery() {
  query.value = ''
  selectedItem.value = null
  hasError.value = false
  isOpen.value = true
  inputRef.value?.focus()
}

function handleBlur() {
  sleep(50).then(() => {
    isOpen.value = false
  })
}

function handleFocus() {
  isOpen.value = true
  hasError.value = false
}

const sharedState = computed(() => ({
  query: query.value,
  isOpen: isOpen.value,
  hasError: hasError.value,
  results: results.value,
  autocompleteCounter: autocomplete.counter.value,
  selectedItem: selectedItem.value,
}))

const sharedHandlers = {
  'update:query': (v: string) => { query.value = v },
  'update:isOpen': (v: boolean) => { isOpen.value = v },
  'select-item': selectItem,
  'find-plan': handleFindPlan,
  keydown: handleKeydown,
  'clear-query': clearQuery,
  blur: handleBlur,
  focus: handleFocus,
}
</script>

<style scoped>
.esim-sticky-navbar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 99999;
  background: rgba(255, 255, 255, 0.95);
  border-bottom: 1px solid rgba(0, 0, 0, 0.10);
  box-shadow: 0 2px 20px rgba(0, 0, 0, 0.10);
  padding: 8px 16px;
  top: 5.5rem;
}

@media screen and (max-width: 640px) {
  .esim-sticky-navbar {
    top: 4.1rem;
  }
}

.esim-sticky-inner {
  max-width: 700px;
  margin: 0 auto;
  min-width: 0;
  overflow: visible;
}
</style>